/** @jsxImportSource @emotion/react */
import { Trans, useLingui } from '@lingui/react/macro';
import { MediaManagerTemplate } from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import { MediaManagerTemplateGroup } from 'modules/manager/domain/interfaces/media-manager-template-group';
import { TEMPLATE_NAME_CONFLICT_MESSAGE, isTemplateNameConflict } from 'modules/manager/domain/template-name-conflict';
import { useUpdateMediaManagerTemplateMutation } from 'modules/manager/store/media-manager-api';
import { modalButton, modalError } from 'modules/template-editor/ui/components/elements/modals/styles';
import { useCallback, useId, useMemo, useState } from 'react';
import UDSelect from 'ud-form/components/select/component';
import { UDText } from 'ud-ui/components';
import UDButton from 'ud-ui/components/button';
import Modal from 'ud-ui/components/modal';

interface Args {
  groups: MediaManagerTemplateGroup[];
}

/**
 * Both groups refetch by the mutation's tags: the target by its id, the one the template leaves
 * because it provides the tags of its templates (IMGM-1515).
 */
function useMoveTemplateModal({ groups }: Args) {
  const { t } = useLingui();
  const [updateTemplate, { isLoading }] = useUpdateMediaManagerTemplateMutation();
  const [template, setTemplate] = useState<MediaManagerTemplate | null>(null);
  const [targetGroupId, setTargetGroupId] = useState<number | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const labelId = useId();
  const options = useMemo(
    () => groups.filter((group) => group.id !== template?.templateGroupId).map((group) => ({ value: group.id, label: group.name })),
    [groups, template],
  );

  const open = useCallback((next: MediaManagerTemplate) => {
    setError(null);
    setTemplate(next);
    setTargetGroupId(null);
    setIsOpen(true);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);

  const onSubmit = useCallback(async () => {
    if (!template || targetGroupId == null) return;
    setError(null);
    try {
      await updateTemplate({ id: template.id, templateGroupId: targetGroupId }).unwrap();
      close();
    } catch (err) {
      console.error('Failed to move media manager template:', err);
      setError(isTemplateNameConflict(err) ? t(TEMPLATE_NAME_CONFLICT_MESSAGE) : t`Не удалось переместить шаблон, попробуйте ещё раз`);
    }
  }, [template, targetGroupId, updateTemplate, close, t]);

  return {
    open,
    close,
    modal: (
      <Modal show={isOpen} onClose={isLoading ? () => undefined : close} size="md">
        <UDText type="title-3" className="mb-5">
          <Trans>Переместить в группу</Trans>
        </UDText>
        <Modal.Body className="flex flex-col gap-3">
          <div className="flex flex-col gap-2">
            <UDText id={labelId} variant="body-3" className="text-gray-400">
              <Trans>Группа</Trans>
            </UDText>
            <UDSelect
              aria-labelledby={labelId}
              options={options}
              value={options.find((option) => option.value === targetGroupId) ?? null}
              placeholder={t`Выберите группу`}
              isDisabled={isLoading}
              onChange={(option) => setTargetGroupId(option?.value ?? null)}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
        </Modal.Body>
        <Modal.Footer justify="start">
          <UDButton
            type="button"
            variant="primary"
            className="flex-center"
            disabled={targetGroupId == null}
            css={modalButton}
            onClick={onSubmit}
          >
            <Trans>Переместить</Trans>
          </UDButton>
          {error && <span css={modalError}>{error}</span>}
        </Modal.Footer>
      </Modal>
    ),
  };
}

export default useMoveTemplateModal;
